import { clientApi, globalApi } from "@/utils/api";

import { TGetMeResDto } from "@/api/services/admin/auth/dtos/get-me.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/admin/auth/dtos/refresh-token.dto";
import { setToken } from "@/utils/token";
import { TLogoutResDto } from "@/api/services/admin/auth/dtos/logout.dto";
import { TVerifyTokenResDto } from "@/api/services/admin/auth/dtos/verify-token.dto";
import { refreshCenterOAuthToken } from "@/features/auth/services/centerOAuthApi";
import Cookies from "js-cookie";

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
  });
};

// Shared refresh promise to prevent concurrent refresh calls (race condition between interceptor, withAuth, SocketProvider)
let refreshPromise: Promise<TRefreshTokenResDto> | null = null;

// Admin sessions are always issued by irohana-center (SSO handoff), so the tokens
// belong to center's Cognito pool and must be refreshed at center — not against
// this account's Cognito (which would always fail and bounce back to center login).
export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  // Reuse in-flight refresh request to avoid race conditions
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const accessToken = Cookies.get("admin-token") || "";
      const data = await refreshCenterOAuthToken(accessToken, payload.refreshToken);
      // Center may not return a new refreshToken — keep the existing one
      setToken(data.accessToken, data.refreshToken || payload.refreshToken);
      return { data } as TRefreshTokenResDto;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const logoutApi = async (): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout");
  return rs?.data;
};

export const verifyTokenApi = async (token: string): Promise<boolean> => {
  const {
    data: { data },
  } = await globalApi.post<TVerifyTokenResDto>("/verify-token", { token });
  return data;
};
